import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Button,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Box,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import axios from '../../api/client';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import ScrollTable from '../ScrollTable';
import TableToolbar from '../TableToolbar';
import useTableSearch from '../../hooks/useTableSearch';

const Notices = ({ propertyId }) => {
  const [notices, setNotices] = useState([]);

  // Client-side: this screen loads every row, so filtering here searches all of
  // them. Screens that page on the server search there instead.
  const {
    query, setQuery, reset,
    rows: visibleNotices, resultCount, totalCount
  } = useTableSearch(notices, {
    searchFields: ['title', 'message', 'category.name'],
    filters: {}
  });
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    noticeType: 'move_out',
    noticeDate: new Date().toISOString().split('T')[0],
    moveOutDate: '',
    reason: '',
  });

  useEffect(() => {
    fetchNotices();
  }, []);

  const fetchNotices = async () => {
    try {
      const response = await axios.get('/notices');
      setNotices(response.data);
    } catch (error) {
      toast.error('Error fetching notices');
    }
  };

  const handleSubmit = async () => {
    try {
      await axios.post('/notices', formData);
      toast.success('Notice submitted');
      setOpen(false);
      setFormData({
        noticeType: 'move_out',
        noticeDate: new Date().toISOString().split('T')[0],
        moveOutDate: '',
        reason: '',
      });
      fetchNotices();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error submitting notice');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      approved: 'success',
      rejected: 'error',
    };
    return colors[status] || 'default';
  };

  const handleViewPdf = async (noticeId) => {
    try {
      const response = await axios.get(`/notices/${noticeId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error viewing PDF');
    }
  };

  const handleDownloadPdf = async (noticeId) => {
    try {
      const response = await axios.get(`/notices/${noticeId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `notice-${noticeId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error downloading PDF');
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Notices</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpen(true)}
        >
          Submit Notice
        </Button>
      </Box>

      <TableToolbar
        search={query}
        onSearchChange={setQuery}
        searchPlaceholder="Title or message"
        onReset={reset}
        resultCount={resultCount}
        totalCount={totalCount}
      />
      <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Notice Date</TableCell>
              <TableCell>Move Out Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleNotices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No notices found
                </TableCell>
              </TableRow>
            ) : (
              visibleNotices.map((notice) => (
                <TableRow key={notice._id}>
                  <TableCell>{notice.noticeType}</TableCell>
                  <TableCell>
                    {format(new Date(notice.noticeDate), 'MMM dd, yyyy')}
                  </TableCell>
                  <TableCell>
                    {notice.moveOutDate
                      ? format(new Date(notice.moveOutDate), 'MMM dd, yyyy')
                      : 'N/A'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={notice.status}
                      color={getStatusColor(notice.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{notice.reason || 'N/A'}</TableCell>
                  <TableCell>
                    {notice.pdfPath && (
                      <Box display="flex" gap={1}>
                        <Button
                          size="small"
                          startIcon={<PictureAsPdfIcon />}
                          onClick={() => handleViewPdf(notice._id)}
                        >
                          View PDF
                        </Button>
                        <Button
                          size="small"
                          startIcon={<DownloadIcon />}
                          onClick={() => handleDownloadPdf(notice._id)}
                        >
                          Download
                        </Button>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </ScrollTable>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Submit Notice</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            select
            label="Notice Type"
            margin="normal"
            value={formData.noticeType}
            onChange={(e) =>
              setFormData({ ...formData, noticeType: e.target.value })
            }
          >
            <MenuItem value="move_out">Move Out</MenuItem>
            <MenuItem value="lease_renewal">Lease Renewal</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </TextField>
          <TextField
            fullWidth
            label="Notice Date"
            type="date"
            margin="normal"
            value={formData.noticeDate}
            onChange={(e) =>
              setFormData({ ...formData, noticeDate: e.target.value })
            }
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            fullWidth
            label="Move Out Date"
            type="date"
            margin="normal"
            value={formData.moveOutDate}
            onChange={(e) =>
              setFormData({ ...formData, moveOutDate: e.target.value })
            }
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            fullWidth
            label="Reason"
            margin="normal"
            multiline
            rows={3}
            value={formData.reason}
            onChange={(e) =>
              setFormData({ ...formData, reason: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default Notices;


